<script lang="ts">
	import { formatMoney, inDollars } from '$lib/utils/money';
	import { getPromoOffAmount } from '$lib/utils/stripe';
	import type { Stripe } from 'stripe';
	import { fade, fly } from 'svelte/transition';
	import type { HTMLAttributes } from 'svelte/elements';

	interface Props extends HTMLAttributes<HTMLDivElement> {
		price: number;
		promoCode?: Stripe.PromotionCode;
		interval?: Stripe.Price.Recurring.Interval;
		suffix?: string;
		subline?: string;
	}

	let { price, promoCode, interval, suffix, subline, class: className, ...other }: Props = $props();

	let amountOff = $derived(promoCode?.coupon ? getPromoOffAmount(price, promoCode?.coupon) : 0);
	let activeCode = $derived(amountOff > 0);
	let discountedPrice = $derived(inDollars(Math.max(price - amountOff, 0)));
</script>

<div
	class={['text-[32px]/[110%] tracking-[-.32px]', className]}
	{...other}
	in:fade
	itemprop="offers"
	itemscope
	itemtype="https://schema.org/Offer"
>
	<meta itemprop="priceCurrency" content="USD" />
	{#if interval === 'month'}
		<div class="max-lg:mb-1">
			{formatMoney(inDollars(price))}

			<span class="text-[20px]/[110%] tracking-[-.2px]"
				>/mo
				{suffix}
			</span>
		</div>
		<div class="text-[12px]/[160%] opacity-50 lg:text-[14px]/[160%]">
			{#if subline}
				{subline.replace('[yearly_price]', formatMoney(inDollars(price * 12)))}
			{/if}
		</div>
	{:else}
		<div class="flex items-center">
			{#if activeCode}
				<p class="text-green-3" transition:fly={{ x: '-25%' }}>
					<span itemprop="price" content={discountedPrice.toFixed(2)}
						>{formatMoney(discountedPrice)}</span
					>
				</p>
			{/if}

			<p class="text-brown-1" class:line-through={activeCode} class:text-[#7C6E67]={activeCode}>
				<span {...activeCode ? {} : { itemprop: 'price', content: inDollars(price).toFixed(2) }}
					>{formatMoney(inDollars(price))}</span
				>
			</p>
		</div>
	{/if}
</div>
